import { useState, useRef, useEffect } from "react";
import { Form, Button, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { checkInActions } from "../store/checkin.slice";
import { toast } from "react-toastify";
import HttpStatus from "../helpers/status.enum";
import SpinnerLoader from "./SpinnerLoader";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";

const START_TIME = 120; // 2-minute OTP countdown

/**
 * Shared modal component for the "Sign on the dotted line" + OTP verification flow.
 *
 * Props:
 *   show               — bool: controls agreement modal visibility
 *   onHide             — func(): called when modal is cancelled/closed
 *   bookingSlug        — string: passed to emailVerification & verifyOtp API calls
 *   cmsContent         — object: needs .otp_description and .signing_agreement_text
 *   isOtpAlreadyVerified — bool: if true, skip email/phone fields and call onSuccess directly
 *   onSuccess          — func(signedName): called after OTP verified (or immediately if already verified)
 *   onBeforeSubmit     — optional func(name, smsEnabled, proceed): pre-submit hook;
 *                        used by checkingin.js for the JotForm gate. Call proceed() to continue.
 *   isSignedTextShow   - Hide show signed name
 *   activeAccordian    - current active accordian number
 */
function AgreementOtpModal({
  show,
  onHide,
  bookingSlug,
  cmsContent,
  isOtpAlreadyVerified,
  onSuccess,
  onBeforeSubmit,
  isSignedTextShow = false,
  activeAccordian = null
}) {
  const dispatch = useDispatch();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isFormValid, setIsFormValid] = useState(false);
  const [otpInputs, setOtpInputs] = useState(["", "", "", "", "", ""]);
  const [userInputOtp, setUserInputOtp] = useState(null);
  const [authCode, setAuthCode] = useState(null);
  const [timer, setTimer] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isFormValidOTP, setIsFormValidOTP] = useState(false);
  // Assumes the signed checkbox is selected when OTP submission occurs from any accordion other than the "Signed on Dotted Line" section
  const [isCheckboxCheckedSign, setIsCheckboxCheckedSign] = useState(false);
  const [checkboxSMSEarlyCheckin, setCheckboxSMSEarlyCheckin] = useState(false);
  const [isSignedButtonDisabled, setIsSignedButtonDisabled] = useState(true);
  const [showOtpModal, setShowOtpModal] = useState(false);

  const inputRefs = useRef([]);

  const activeKeyStep = {
    HOUSE_RULE: 0,
    RENTAL_AGREEMENT: 1,
    GUEST_ID_VERIFICATION: 2,
    PAYMENT_CARD: 3,
    GUEST_UPGRADES: 4,
  }

  // OTP countdown timer
  useEffect(() => {
    let interval;
    if (showOtpModal && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [showOtpModal, timer]);

  const validateForm = (phoneValue, emailValue) => {
    const phoneValid = /^\+?\d{7,15}$/.test(phoneValue.trim());
    const emailValid =
      emailValue.trim() !== "" && /\S+@\S+\.\S+/.test(emailValue);
    setIsFormValid(phoneValid && emailValid);
  };

  const handleNameChange = (event) => {
    const value = event.target.value;
    setName(value);
    // Here When OTP ask from different accordian in case rental is not allowed then
    setIsSignedButtonDisabled((!isCheckboxCheckedSign && isSignedTextShow) || !value.trim());
  };

  const handlePhoneChange = (value) => {
    setPhone(value);
    setPhoneError("");
    validateForm(value, email);
  };

  const handleEmailChange = (event) => {
    const { value } = event.target;
    setEmail(value);
    setEmailError("");
    validateForm(phone, value);
  };

  const handleCheckboxChange = (event) => {
    const { checked } = event.target;
    setIsCheckboxCheckedSign(checked);
    setIsSignedButtonDisabled(!checked || !name.trim());
  };

  const handle2faSubmit = async () => {
    setIsLoading(true);
    setIsFormValid(false);

    const formData = {
      email,
      phone_number: phone,
      booking_slug: bookingSlug,
      is_from_rental_agreement: isSignedTextShow ? 1 : 0,
    };

    if(activeAccordian == activeKeyStep.HOUSE_RULE){
      formData.is_from_id_verification = 1
    }
    if(activeAccordian == activeKeyStep.PAYMENT_CARD){
      formData.is_from_payment_card = 1
    }

    try {
      const { payload } = await dispatch(
        checkInActions.emailVerification(formData),
      );

      if (payload?.status === HttpStatus.HTTP_OK) {
        const authCode = payload?.result?.auth_code;

        setAuthCode(authCode);
        setShowOtpModal(true);
        setTimer(START_TIME);
      } else {
        toast(payload?.message);
      }
    } catch (error) {
      console.error("Error occurred while submitting form:", error);
    } finally {
      // Ensures these always run (no repetition)
      setIsLoading(false);
      setIsFormValid(true);
    }
  };

  const handleResendOTP = async (event) => {
    event.preventDefault();
    const formData = {
      verification_code: "",
      is_resend: 1,
      auth_code: authCode,
      booking_slug: bookingSlug,
    };
    try {
      const result = await dispatch(checkInActions.verifyOtp(formData));
      if (result?.payload?.status === HttpStatus.HTTP_OK) {
        setOtpInputs(["", "", "", "", "", ""]);
        setTimer(START_TIME);
      }
    } catch (error) {
      console.error("Error occurred while submitting form:", error);
    }
  };

  const handleOtpInputChange = (index, value) => {
    if (/^\d$/.test(value) || value === "") {
      const updated = [...otpInputs];
      updated[index] = value;
      setOtpInputs(updated);
      if (value === "" && index > 0) {
        inputRefs.current[index - 1].focus();
      } else if (index < inputRefs.current.length - 1 && value.trim() !== "") {
        inputRefs.current[index + 1].focus();
      }
      const allFilled = updated.every((input) => input.trim() !== "");
      setIsFormValidOTP(allFilled);
      setUserInputOtp(updated.join(""));
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");
    if (pastedData.length === otpInputs.length && /^\d+$/.test(pastedData)) {
      const updated = pastedData.split("");
      setOtpInputs(updated);
      setIsFormValidOTP(true);
      setUserInputOtp(pastedData);
      inputRefs.current[otpInputs.length - 1].focus();
    }
  };

  const closeOtpModal = () => {
    setShowOtpModal(false);
    setOtpInputs(["", "", "", "", "", ""]);
  };

  const handleOtpSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setIsFormValidOTP(false);
    const formData = {
      verification_code: userInputOtp,
      is_resend: 0,
      booking_slug: bookingSlug,
      auth_code: authCode,
      is_sms_enabled: checkboxSMSEarlyCheckin,
    };
    try {
      const result = await dispatch(checkInActions.verifyOtp(formData));
      if (result?.payload?.status === HttpStatus.HTTP_OK) {
        setIsFormValidOTP(true);
        setShowOtpModal(false);
        onSuccess(name);
      } else {
        toast(result?.payload?.message);
        setIsFormValidOTP(true);
      }
    } catch (error) {
      setIsFormValidOTP(true);
      console.error("Error occurred while submitting form:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignedSubmit = (event) => {
    event.preventDefault();
    document.body.classList.add("signOndotted");
    const proceed = () => {
      if (isOtpAlreadyVerified) {
        onSuccess(name);
      } else {
        handle2faSubmit();
      }
    };
    if (onBeforeSubmit) {
      onBeforeSubmit(name, checkboxSMSEarlyCheckin, proceed);
    } else {
      proceed();
    }
  };
  // Disable submit when name/checkbox invalid, or (if OTP not verified) email/phone invalid
  const isSubmitDisabled = isSignedButtonDisabled || isLoading || !isFormValid;

  return (
    <>
      {/* Agreement / Sign on the dotted line Modal */}
      <Modal
        show={show}
        onHide={onHide}
        dialogClassName="modal-90w modal-bottom"
        aria-labelledby="example-custom-modal-styling-title"
        className="agreement agreement-d-block"
        backdrop="static"
      >
        <Modal.Header closeButton onClick={onHide}></Modal.Header>
        <Modal.Body>
          <Form autoComplete="off" onSubmit={handleSignedSubmit}>
            <h2>Signed</h2>
            <Form.Group className="mb-3" controlId="agreementName">
              <Form.Control
                type="text"
                placeholder="Your name"
                value={name}
                onChange={handleNameChange}
                required
                disabled={isLoading}
                autoComplete="off"
              />
            </Form.Group>

            {isOtpAlreadyVerified === false && (
              <>
                <Form.Group className="mb-3" controlId="agreementEmail">
                  <Form.Control
                    type="email"
                    className="input-text2"
                    placeholder="Please enter email for verification"
                    value={email}
                    onChange={handleEmailChange}
                    required
                    disabled={isLoading}
                    autoComplete="off"
                  />
                  <Form.Text className="text-danger">{emailError}</Form.Text>
                </Form.Group>
                <Form.Group className="mb-3" controlId="agreementPhone">
                  <PhoneInput
                    country={"us"}
                    value={phone}
                    className="input-text2"
                    onChange={handlePhoneChange}
                    inputProps={{
                      required: true,
                      type: "text",
                      autoComplete: "off",
                      autoCorrect: "off",
                      autoCapitalize: "off",
                      spellCheck: false,
                      inputMode: "tel",
                    }}
                    placeholder="1 (702) 123-4567"
                    disabled={isLoading}
                  />
                  <Form.Text className="text-danger">{phoneError}</Form.Text>
                </Form.Group>
                <Form.Group
                  className="mb-3 smsCheckbox"
                  controlId="agreementSms"
                >
                  <div className="d-flex align-items-start">
                    <label className="custom-checkbox">
                      <input
                        type="checkbox"
                        onClick={() =>
                          setCheckboxSMSEarlyCheckin((prev) => !prev)
                        }
                        disabled={isLoading}
                      />
                      <span className="checkmark"></span>
                    </label>
                    <div className="checkBox-text">
                      <span
                        className="checkBox-text-heading"
                        dangerouslySetInnerHTML={{
                          __html: cmsContent?.checkin_otp,
                        }}
                      ></span>
                    </div>
                  </div>
                </Form.Group>
              </>
            )}

            {isSignedTextShow && (
              <Form.Group className="mb-3" controlId="agreementCheckbox">
                <div className="d-flex align-items-start">
                  <label className="custom-checkbox">
                    <input
                      type="checkbox"
                      checked={isCheckboxCheckedSign}
                      onChange={handleCheckboxChange}
                      disabled={isLoading}
                    />
                    <span className="checkmark"></span>
                  </label>
                  {cmsContent?.signing_agreement_text && (
                    <div className="checkBox-text">
                      <span
                        className="checkBox-text-heading"
                        dangerouslySetInnerHTML={{
                          __html: `${cmsContent.signing_agreement_text} <a href="javascript:void(0)" class="checkBox-text-heading-link">here</a>`,
                        }}
                      ></span>
                    </div>
                  )}
                </div>
              </Form.Group>
            )}

            <div className="d-flex justify-content-between">
              <Button className="outlinebutton" onClick={onHide}>
                Cancel
              </Button>
              <Button
                className="fillbutton"
                type="submit"
                disabled={isSubmitDisabled}
              >
                {isLoading ? <SpinnerLoader /> : "Signed!"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* OTP Verification Modal */}
      <Modal
        show={showOtpModal}
        onHide={closeOtpModal}
        dialogClassName="modal-90w modal-bottom"
        aria-labelledby="example-custom-modal-styling-title"
        className="otp-modal"
        backdrop="static"
      >
        <Modal.Header closeButton onClick={closeOtpModal}></Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleOtpSubmit}>
            <h2 className="mb-2">Verification</h2>
            <h6
              className="mb-2 pb-2"
              dangerouslySetInnerHTML={{
                __html: `${cmsContent?.otp_description}`,
              }}
            ></h6>
            <Form.Group className="mb-4" controlId="otpnumber">
              <div className="d-flex justify-content-center gap-2">
                {otpInputs.map((input, index) => (
                  <Form.Control
                    key={index}
                    type="tel"
                    className="input-text-otp"
                    value={input}
                    onChange={(e) =>
                      handleOtpInputChange(index, e.target.value)
                    }
                    maxLength={1}
                    ref={(ref) => (inputRefs.current[index] = ref)}
                    required
                    onPaste={index === 0 ? handleOtpPaste : null}
                  />
                ))}
              </div>
            </Form.Group>
            <div className="d-flex justify-content-center">
              <Button
                className="fillbutton"
                type="submit"
                disabled={!isFormValidOTP}
              >
                {isLoading ? <SpinnerLoader /> : "Verify"}
              </Button>
            </div>
            <div className="d-flex justify-content-center mt-3">
              <p>
                {timer === 0 ? (
                  <Link
                    to="#"
                    className="text-overline2"
                    onClick={handleResendOTP}
                  >
                    Resend Code
                  </Link>
                ) : (
                  `Resend code in ${timer} seconds`
                )}
              </p>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default AgreementOtpModal;
